import { Injectable } from '@angular/core';
import { ProjectService } from './project.service';
import { EMPTY, from, Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

/**
 * Compute a URL-safe slug from a role name.
 * Must match the backend logic in Role::generateSlug()
 */
export function generateRoleSlug(roleName: string): string {
    return roleName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export interface MentionUser {
    unixName: string;
    fullName?: string;
    firstname?: string;
    lastname?: string;
}

export interface MentionRole {
    roleSlug: string;
    roleName: string;
}

export interface MentionItem {
    unixName: string;
    displayName?: string;
    fullName?: string;
    firstname?: string;
    lastname?: string;
    isRole?: boolean;
    roleSlug?: string;
    roleName?: string;
}

/**
 * The real "gforge" system account (#65187). It is not a project member or a
 * thread participant, so it would never surface from a surface's own roster.
 * Offered unconditionally so @gforge is discoverable everywhere, regardless of
 * whether AI is enabled for the project -- the poller replies with a canned
 * message when it isn't.
 */
export const GFORGE_MENTION_USER: MentionUser = {
    unixName: 'gforge',
    fullName: 'GForge',
    firstname: 'GForge',
    lastname: 'System'
};

/**
 * Chat's "@all" pseudo-user. Not an account: ChatRenderService.hasMention()
 * treats "@all" as mentioning everyone in the room but the author. Only chat
 * understands it, so only chat offers it.
 */
export const ALL_MENTION_USER: MentionUser = {
    unixName: 'all',
    fullName: 'Everyone in this chat'
};

export interface MentionSearchOptions {
    debounceMs?: number;
    minSearchLength?: number;
    maxResults?: number;
}

export interface MentionListConfig {
    items: MentionItem[];
    triggerChar: string;
    labelKey: string;
    maxItems?: number;
    disableSearch?: boolean;
    mentionSelect: (item: MentionItem) => string;
}

/**
 * A live mention source: everything a surface needs, and the whole of what it
 * has to hold. Bind `list` to the editor's `[mentions]`, feed its
 * `(searchTerm)` into `search()`, and `destroy()` on teardown.
 *
 * This is deliberately the binding itself rather than something a host copies
 * into its own field -- a parallel `mentionList` on every host was five copies
 * of the same plumbing, and the thing that drifted.
 */
export interface MentionConfig {
    /**
     * The current list, for `[mentions]`. Replaced -- never mutated -- on each
     * result set, because the editor picks changes up through ngOnChanges.
     */
    readonly list: MentionListConfig[];

    search: (term: string) => void;

    /**
     * Re-run the current query. For hosts whose candidates arrive after the
     * source is built -- chat fetches project roles asynchronously, so the
     * first '@' would otherwise show a roleless list until the next keystroke.
     */
    refresh: () => void;

    destroy: () => void;
}

/**
 * Describes one mention-enabled surface. Everything is a thunk, read at search
 * time rather than captured, for two reasons:
 *
 *  - chat's candidate list changes on every poll, and rebuilding the config
 *    each time was the old duplication;
 *  - a host's project is often still loading when it builds its source, so a
 *    snapshot would strand the surface on an empty value forever. That is what
 *    the `if (!project) return;` guard in every host used to paper over.
 */
export interface MentionSourceOptions {
    /** Candidates this surface always offers, e.g. tracker assignees or thread participants. */
    seed?: () => MentionUser[];

    /** Project roles offered alongside users. */
    roles?: () => MentionRole[];

    /**
     * Project to search for members not covered by `seed`, by unix name or id.
     * Return '' to stay local-only.
     */
    project?: () => string;

    /** Tuning for debounce / minimum query length / result cap. */
    search?: Partial<MentionSearchOptions>;
}

@Injectable()
export class MentionsService {

    // Default search options
    private readonly DEFAULT_SEARCH_OPTIONS: MentionSearchOptions = {
        debounceMs: 300,
        minSearchLength: 2,
        maxResults: 12
    };

    constructor(
        private projectService: ProjectService
    ) {}

    /**
     * Build a combined list of user and role mention items.
     */
    static buildMentionItems(users: MentionUser[], roles: MentionRole[]): MentionItem[] {
        const userItems: MentionItem[] = (users || []).map(u => ({
            ...u,
            displayName: u.unixName,
            isRole: false
        }));
        const roleItems: MentionItem[] = (roles || []).map(r => ({
            unixName: r.roleSlug,
            displayName: r.roleName,
            fullName: `Role: ${ r.roleName }`,
            isRole: true,
            roleSlug: r.roleSlug,
            roleName: r.roleName
        }));
        return [...userItems, ...roleItems];
    }

    /**
     * The one matching rule for @-mentions.
     *
     * Matches across every name a person might type -- unix name, display
     * name, full name, first/last name, role name and role slug -- and ranks
     * prefix hits above mid-string hits so typing "jd" puts `jdoe` at the top
     * rather than someone whose surname merely contains it. Ties break on
     * unix name for a stable order.
     *
     * Because this ranking is the point, configs built here set
     * `disableSearch: true`: the editor renders the list as handed to it
     * instead of re-filtering it with a narrower rule and re-sorting it
     * alphabetically.
     */
    static match(items: MentionItem[], searchTerm: string, maxResults: number = 12): MentionItem[] {
        if (!items?.length) return [];

        const byUnixName = (a: MentionItem, b: MentionItem) =>
            String(a.unixName).localeCompare(String(b.unixName));

        if (!searchTerm) {
            return [...items].sort(byUnixName).slice(0, maxResults);
        }

        const term = searchTerm.toLowerCase();
        const fieldsOf = (item: MentionItem) => [
            item.unixName, item.displayName, item.fullName,
            item.firstname, item.lastname, item.roleSlug, item.roleName
        ].map(v => (v || '').toLowerCase());

        const prefix: MentionItem[] = [];
        const substring: MentionItem[] = [];

        for (const item of items) {
            const fields = fieldsOf(item);
            if (fields.some(f => f.startsWith(term))) {
                prefix.push(item);
            } else if (fields.some(f => f.includes(term))) {
                substring.push(item);
            }
        }

        return [...prefix.sort(byUnixName), ...substring.sort(byUnixName)].slice(0, maxResults);
    }

    /**
     * Maps user object to MentionUser object
     * @param user Raw user object
     * @returns MentionUser object with unixName, fullName, firstname, lastname
     */
    mapToMentionUser(user: any): MentionUser {
        const firstname = user.firstname || user.firstName || '';
        const lastname = user.lastname || user.lastName || '';
        const fullName = user.fullName || `${ firstname } ${ lastname }`.trim();

        return {
            unixName: user.unixName,
            fullName: fullName,
            firstname: firstname,
            lastname: lastname
        };
    }

    /**
     * Create a mention source
     *
     * State lives in this closure
     */
    createMentionConfig(options: MentionSourceOptions): MentionConfig {
        const searchOptions = { ...this.DEFAULT_SEARCH_OPTIONS, ...(options.search || {}) };
        const debounceMs = searchOptions.debounceMs ?? 300;
        const maxResults = searchOptions.maxResults ?? 12;
        const minSearchLength = searchOptions.minSearchLength ?? 2;

        // Members discovered through the API, kept for the lifetime of this
        // surface only. Never shared with any other mention source.
        let fetched: MentionUser[] = [];

        const seedUsers = (): MentionUser[] =>
            (options.seed ? options.seed() : []).filter(u => u?.unixName);

        const roles = (): MentionRole[] =>
            (options.roles ? options.roles() : [])
                .filter(r => r?.roleName)
                .map(r => ({
                    roleSlug: r.roleSlug || generateRoleSlug(r.roleName),
                    roleName: r.roleName
                }));

        const candidates = (): MentionItem[] => {
            const users = [...seedUsers(), ...fetched];
            const seen = new Set<string>();
            const unique = users.filter(u => {
                if (seen.has(u.unixName)) return false;
                seen.add(u.unixName);
                return true;
            });
            return MentionsService.buildMentionItems(unique, roles());
        };

        const listFor = (items: MentionItem[]): MentionListConfig[] => ([{
            items,
            triggerChar: '@',
            labelKey: 'displayName',
            maxItems: maxResults,
            // We filtered and ranked; the editor renders what it is given.
            disableSearch: true,
            mentionSelect: (item: MentionItem) => item.isRole ? `@${ item.roleSlug } ` : `@${ item.unixName } `
        }]);

        const localMatches = (searchTerm: string): MentionItem[] =>
            MentionsService.match(candidates(), searchTerm, maxResults);

        // Only reach for the API when the surface's own people cannot answer
        // the query -- someone who is not a participant yet.
        const needsLookup = (searchTerm: string, matches: MentionItem[]): boolean => {
            const noLocalUser = !matches.some(m => !m.isRole);
            const project = options.project ? options.project() : '';
            return noLocalUser && searchTerm.length >= minSearchLength && !!project;
        };

        // null cancels any lookup still waiting out its debounce or in flight.
        const lookupSubject = new Subject<string | null>();
        let lastTerm = '';
        let destroyed = false;

        const publish = (items: MentionItem[]) => {
            source.list = listFor(items);
        };

        // Local matching is synchronous and cheap, so it runs on every keystroke with no debounce
        const run = (term: string) => {
            if (destroyed) return;
            lastTerm = term || '';
            const matches = localMatches(lastTerm);
            publish(matches);
            lookupSubject.next(needsLookup(lastTerm, matches) ? lastTerm : null);
        };

        const source = {
            list: [] as MentionListConfig[],
            search: (term: string) => run(term),
            refresh: () => run(lastTerm),
            destroy: () => {
                destroyed = true;
                lookupSubject.complete();
                lookupSubscription.unsubscribe();
            }
        };

        // Only the network lookup is debounced.
        const lookupSubscription: Subscription = lookupSubject.pipe(
            debounceTime(debounceMs),
            switchMap((searchTerm: string | null) => {
                if (searchTerm === null) return EMPTY;
                const project = options.project ? options.project() : '';
                return from(
                    this.fetchProjectMembers(project, searchTerm)
                        // Leave the local matches standing; a failed lookup
                        // should not empty a dropdown that already has
                        // something in it.
                        .catch(() => [] as MentionUser[])
                        .then(apiUsers => ({ searchTerm, apiUsers }))
                );
            })
        ).subscribe(({ searchTerm, apiUsers }) => {
            if (!apiUsers.length) return;
            fetched = [...fetched, ...apiUsers];
            // The user may have typed on while the request was out; the
            // members are cached either way, but only repaint for the query
            // that is actually showing.
            if (searchTerm === lastTerm) {
                publish(localMatches(lastTerm));
            }
        });

        // Show the surface's own people before anything is typed.
        run('');

        return source;
    }

    private async fetchProjectMembers(projectUnixName: string, searchTerm: string = ''): Promise<MentionUser[]> {
        const queryParams: any = {
            page_num: 0,
            page_size: 20,
            groups: true,
            group: true,
            rel: 'roles,groups'
        };

        if (searchTerm) {
            queryParams.search = searchTerm;
        }

        const response: any = await this.projectService.members(projectUnixName, queryParams);
        const items = response?.items || [];

        return items
            .filter((member: any) => member?.unixName)
            .map((member: any) => this.mapToMentionUser(member));
    }
}
